// Peeks for the Analyses list (DESIGN_SPEC §12.17). A job record lags its run: the backend learns
// "dispatched" and, later, the terminal state, and it never writes a verdict into the record. So
// for rows whose record cannot speak for itself the list reads what the run itself says:
//   live   record queued/dispatched/running on a node: its SSE stream (through the shared stream
//          registry: one connection per job, shared with the report page) while the row is visible
//          and the run is live → effective state, event count, T0
//   once   record completed or cancelled on a node without a verdict: the behavior report first
//          (one small JSON); the stream only when the report has no verdict or a cancelled run's
//          phase is unknown. A stream is read until it ends, stalls (quiet), fails or a cap passes.
// At most 4 streams at once (3 of them live) and 4 report reads: HTTP/1.1 gives a host six
// connections and the page's own polling needs one. A finished peek closes its connection at once
// (no release grace), so the cap holds. Results are kept for the session; a finished once-peek is
// never repeated for the same record state.
//   createPeeker(deps) → { sync(jobs), visible(id, on), read(id), subscribe(fn), version(), setLoader(fn), stop() }
//   peeker             the app-wide instance (stream registry; useEffectiveJobs sets the report loader)
import { streams, type StreamEntry } from "@/hooks/streamRegistry";
import { asEffective, deriveEffective, isTerminal, TERMINAL, type Snapshot } from "@/lib/eventStore";
import type { VerdictSnap } from "@/lib/derive/types";
import type { EffectiveState, Job, JobState } from "@/types";

export type PeekStatus = "none" | "waiting" | "open" | "done";

/** What a peek learned. Times are epoch ms. */
export type PeekInfo = {
  /** The record state this was read against. */
  recordState: JobState;
  /** The latest job.state on the stream (null: none seen), when it was emitted and its reason. */
  streamState: EffectiveState | null;
  stateAt: number | null;
  reason?: string;
  /** The last non-terminal state the stream reported (a cancelled run stopped there). */
  phase: EffectiveState | null;
  /** The verdict event on the stream. */
  verdict: VerdictSnap | null;
  /** behavior-report.json's verdict: undefined = not read, null = none. */
  reportVerdict?: VerdictSnap | null;
  /** Events received (null: the stream was not read). */
  events: number | null;
  lastEventAt: number | null;
  t0: number | null;
  /** When the stream's terminal state was emitted. */
  endAt: number | null;
  ended: boolean;
  /** The stream could not be read at all. */
  streamFailed?: boolean;
};

export type PeekRead = { status: PeekStatus; info?: PeekInfo };

type ReportLoader = (job: Job) => Promise<VerdictSnap | null>;

type Registry = {
  entryFor(id: string): StreamEntry;
  acquire(entry: StreamEntry): void;
  release(entry: StreamEntry, opts?: { graceMs?: number }): void;
  setRecord(entry: StreamEntry, record: Job | undefined): void;
};

export type PeekDeps = {
  registry: Registry;
  loader?: ReportLoader;
  now?: () => number;
  maxStreams?: number;
  maxLive?: number;
  maxReports?: number;
  /** A once-stream that has delivered events and then nothing for this long is finished. */
  quietMs?: number;
  /** A once-stream with no event at all after this long is given up. */
  firstEventMs?: number;
  /** Hard cap for a once-stream. */
  capMs?: number;
  /** A live peek whose row left the viewport is released after this long. */
  hiddenGraceMs?: number;
  /** Subscribers hear about changes at most this often. */
  publishMs?: number;
};

type Kind = "live" | "once";
type Want = { job: Job; kind: Kind; order: number };
type Slot = {
  id: string;
  kind: Kind;
  entry: StreamEntry;
  unsub: () => void;
  started: number;
  lastSeq: number;
  lastAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  hide: ReturnType<typeof setTimeout> | null;
};

/** The node only serves ids of this shape; a record holding anything else answers 400 to every read. */
const NODE_ID = /^[a-z0-9-]{1,64}$/;

/** Which peek a record needs, if any. Never-dispatched jobs have no stream (the backend answers 409). */
export function peekKind(job: Job): Kind | null {
  if (!job.node_id) return null;
  if (job.remote_job_id && !NODE_ID.test(job.remote_job_id)) return null;
  if (!isTerminal(job.state)) return "live";
  if ((job.state === "completed" || job.state === "cancelled") && !job.verdict) return "once";
  return null;
}

/** A record reason that already names where a cancelled run stopped ("cancelled during boot"). */
const reasonNamesPhase = (reason: string | undefined): boolean => /^cancel/i.test(reason?.trim() ?? "");

function blank(job: Job): PeekInfo {
  return {
    recordState: job.state,
    streamState: null,
    stateAt: null,
    phase: null,
    verdict: null,
    events: null,
    lastEventAt: null,
    t0: null,
    endAt: null,
    ended: false,
  };
}

const finite = (n: number | null | undefined): number | null => (typeof n === "number" && Number.isFinite(n) ? n : null);

/** The stream store, read against the record (the same effective-state rules as the report). */
function infoFromSnapshot(job: Job, snap: Snapshot, prev?: PeekInfo): PeekInfo {
  const eff = deriveEffective(snap, job);
  let phase: EffectiveState | null = null;
  let endAt: number | null = null;
  for (const t of snap.transitions) {
    if (TERMINAL.has(t.state)) {
      if (endAt === null) endAt = finite(t.ts);
    } else if (endAt === null) phase = t.state;
  }
  return {
    recordState: job.state,
    streamState: asEffective(snap.state?.state),
    stateAt: finite(snap.state?.ts),
    reason: snap.state?.reason || undefined,
    phase,
    verdict: snap.verdict,
    reportVerdict: prev?.reportVerdict,
    events: snap.events.length,
    lastEventAt: snap.lastTs,
    t0: eff.t0,
    endAt,
    ended: snap.ended,
    streamFailed: prev?.streamFailed,
  };
}

export function createPeeker(deps: PeekDeps) {
  const now = deps.now ?? Date.now;
  const maxStreams = deps.maxStreams ?? 4;
  const maxLive = deps.maxLive ?? 3;
  const maxReports = deps.maxReports ?? 4;
  const quietMs = deps.quietMs ?? 2500;
  const firstEventMs = deps.firstEventMs ?? 8000;
  const capMs = deps.capMs ?? 12_000;
  const hiddenGraceMs = deps.hiddenGraceMs ?? 5000;
  const publishMs = deps.publishMs ?? 250;
  let loader = deps.loader;

  const infos = new Map<string, PeekInfo>();
  const settled = new Map<string, JobState>(); // id → the record state its final result belongs to
  const reports = new Map<string, "loading" | "done">();
  const hidden = new Set<string>(); // rows reported off screen (rows count as visible until told otherwise)
  const slots = new Map<string, Slot>();
  const listeners = new Set<() => void>();
  let wants = new Map<string, Want>();
  let reportsInFlight = 0;
  let version = 0;
  let publishTimer: ReturnType<typeof setTimeout> | null = null;
  let pumpQueued = false;

  function publish() {
    if (publishTimer) return;
    publishTimer = setTimeout(() => {
      publishTimer = null;
      version++;
      listeners.forEach((l) => l());
    }, publishMs);
  }

  function queuePump() {
    if (pumpQueued) return;
    pumpQueued = true;
    queueMicrotask(pump);
  }

  const isSettled = (w: Want) => settled.get(w.job.id) === w.job.state;

  /** A once-row needs the stream unless the report gave a verdict and nothing else is missing. */
  function streamWanted(w: Want): boolean {
    if (!loader) return true;
    if (reports.get(w.job.id) !== "done") return false;
    const rv = infos.get(w.job.id)?.reportVerdict;
    if (!rv) return true;
    return w.job.state === "cancelled" && !reasonNamesPhase(w.job.reason);
  }

  function pump() {
    pumpQueued = false;
    for (const s of [...slots.values()]) {
      const w = wants.get(s.id);
      if (!w || w.kind !== s.kind) {
        stop(s);
        continue;
      }
      if (s.kind !== "live") continue;
      if (hidden.has(s.id)) {
        if (!s.hide)
          s.hide = setTimeout(() => {
            s.hide = null;
            if (slots.get(s.id) === s && hidden.has(s.id)) {
              stop(s);
              queuePump();
            }
          }, hiddenGraceMs);
      } else if (s.hide) {
        clearTimeout(s.hide);
        s.hide = null;
      }
    }

    // visible rows first, then list order (newest first)
    const order = [...wants.values()].sort((a, b) => Number(hidden.has(a.job.id)) - Number(hidden.has(b.job.id)) || a.order - b.order);

    for (const w of order) {
      if (reportsInFlight >= maxReports) break;
      if (w.kind === "once" && loader && !reports.has(w.job.id) && !isSettled(w)) startReport(w);
    }

    for (const w of order) {
      if (w.kind === "once" && !isSettled(w) && !slots.has(w.job.id) && reports.get(w.job.id) === "done" && !streamWanted(w)) {
        settled.set(w.job.id, w.job.state);
        publish();
      }
    }

    let live = 0;
    for (const s of slots.values()) if (s.kind === "live") live++;
    for (const w of order) {
      if (slots.size >= maxStreams) break;
      if (slots.has(w.job.id) || isSettled(w)) continue;
      if (w.kind === "live") {
        if (hidden.has(w.job.id) || live >= maxLive) continue;
        start(w);
        live++;
      } else if (streamWanted(w)) start(w);
    }
  }

  function startReport(w: Want) {
    const id = w.job.id;
    const load = loader;
    if (!load) return;
    reports.set(id, "loading");
    reportsInFlight++;
    const done = (v: VerdictSnap | null) => {
      reportsInFlight--;
      reports.set(id, "done");
      infos.set(id, { ...(infos.get(id) ?? blank(w.job)), reportVerdict: v });
      publish();
      queuePump();
    };
    load(w.job).then(done, () => done(null));
  }

  function start(w: Want) {
    const id = w.job.id;
    const entry = deps.registry.entryFor(id);
    const t = now();
    const s: Slot = { id, kind: w.kind, entry, unsub: () => undefined, started: t, lastSeq: -1, lastAt: t, timer: null, hide: null };
    slots.set(id, s);
    deps.registry.setRecord(entry, w.job);
    s.unsub = entry.store.subscribe(() => onStore(s));
    deps.registry.acquire(entry);
    onStore(s);
    if (slots.get(id) === s && s.kind === "once") arm(s);
    publish();
  }

  function onStore(s: Slot) {
    if (slots.get(s.id) !== s) return;
    const w = wants.get(s.id);
    if (!w) return;
    const snap = s.entry.store.getSnapshot();
    if (snap.lastSeq !== s.lastSeq) {
      s.lastSeq = snap.lastSeq;
      s.lastAt = now();
    }
    const info = infoFromSnapshot(w.job, snap, infos.get(s.id));
    infos.set(s.id, info);
    publish();
    if (s.kind === "once") {
      const failed = snap.connection === "reconnecting";
      if (snap.ended || (isTerminal(snap.state?.state) && !!snap.verdict) || snap.connection === "closed" || failed) {
        finish(s, failed && snap.events.length === 0);
      }
    } else if (snap.ended || isTerminal(info.streamState)) finish(s);
  }

  /** Once-streams: stop when quiet after data, when nothing arrives at all, or at the cap. */
  function arm(s: Slot) {
    const tick = () => {
      s.timer = null;
      if (slots.get(s.id) !== s) return;
      const t = now();
      const got = s.lastSeq > 0;
      if (t - s.started >= capMs || (!got && t - s.started >= firstEventMs) || (got && t - s.lastAt >= quietMs)) {
        finish(s, !got);
        return;
      }
      s.timer = setTimeout(tick, Math.min(250, quietMs));
    };
    s.timer = setTimeout(tick, Math.min(250, quietMs));
  }

  function finish(s: Slot, failed = false) {
    const w = wants.get(s.id);
    if (failed && w) infos.set(s.id, { ...(infos.get(s.id) ?? blank(w.job)), streamFailed: true });
    if (w) settled.set(s.id, w.job.state);
    stop(s);
    queuePump();
  }

  /** keep: the list is going away (a report may take the same stream over), so the registry's grace applies. */
  function stop(s: Slot, keep = false) {
    if (slots.get(s.id) !== s) return;
    slots.delete(s.id);
    if (s.timer) clearTimeout(s.timer);
    if (s.hide) clearTimeout(s.hide);
    s.unsub();
    deps.registry.release(s.entry, keep ? undefined : { graceMs: 0 });
    publish();
  }

  return {
    /** The list's current jobs, newest first. Starts, keeps and stops peeks to match. */
    sync(jobs: readonly Job[]) {
      const next = new Map<string, Want>();
      jobs.forEach((job, order) => {
        const kind = peekKind(job);
        if (kind) next.set(job.id, { job, kind, order });
      });
      wants = next;
      for (const s of [...slots.values()]) {
        const w = wants.get(s.id);
        if (!w) continue;
        deps.registry.setRecord(s.entry, w.job);
        onStore(s);
      }
      queuePump();
    },

    /** A row entered (true) or left (false) the viewport. */
    visible(id: string, on: boolean) {
      if (on === !hidden.has(id)) return;
      if (on) hidden.delete(id);
      else hidden.add(id);
      queuePump();
    },

    read(id: string): PeekRead {
      const w = wants.get(id);
      const info = infos.get(id);
      if (!w) return { status: info ? "done" : "none", info };
      if (slots.has(id)) return { status: "open", info };
      if (isSettled(w)) return { status: "done", info };
      if (w.kind === "live" && hidden.has(id)) return { status: "none", info };
      return { status: "waiting", info };
    },

    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },

    version: () => version,

    setLoader(fn: ReportLoader | undefined) {
      loader = fn;
      queuePump();
    },

    /** Release every peek (the list unmounted). What was learned stays cached. */
    stop() {
      for (const s of [...slots.values()]) stop(s, true);
      wants = new Map();
      hidden.clear();
    },

    /** Test hook. */
    active: () => [...slots.values()].map((s) => `${s.kind}:${s.id}`),
  };
}

type Peeker = ReturnType<typeof createPeeker>;

export const peeker: Peeker = createPeeker({ registry: streams });
